/*
 * HomeCrest Layer 5 - Customise flow controller
 * ===========================================================================
 * Drives the modal as a sequence of states:
 *
 *   options -> describe -> [clarify] -> design -> submit -> contact -> sent
 *                              |          | ^                 |
 *                              +----------+ +--- refinement --+
 *                                         |
 *                                       failed -> retry / talk to HomeCrest
 *
 * OPEN-ENDED. Anything the customer types is attempted. There is no capability
 * message and no "not offered" path anywhere in this file.
 *
 * ITERATIVE. Each refinement APPENDS to an ordered design stack, producing a
 * new configuration_hash. The existing Layer 4 reconciliation then makes the
 * previous visualisation stale automatically - that machinery is untouched.
 *
 * A FAILED VISUALISATION IS NOT A DESIGN. `submit` is reachable only when
 * Layer 5's `canSubmit` passes, which requires a READY preview still bound to
 * the current hash. A failure routes to help, never to submission.
 */

const STEPS = ['options', 'describe', 'clarify', 'design', 'failed', 'submit', 'contact', 'sent'];

const S = {
  step: 'options',
  stack: [],          // ordered modifications - the design so far
  selections: [],
  configuration: null,
  preview: null,
  lastRequest: '',
  clarification: null,
  busy: false,
  /* Cost control. Every PAID generation this drawer session has started.
     The authoritative limit is the Worker's - this is the local half, so the
     customer is told before a request is even sent. */
  generations: 0,

  /* ---------------------------------------------------------------- viewer */
  /* Pure presentation. Nothing below is ever sent anywhere, and nothing below
     can start a generation - it decides only what the customer is looking at.

     `originalImage` is the PDP product photograph the whole design descends
     from. It is captured from the page on every generation and is NEVER
     replaced by a result, so the comparison is always
     ORIGINAL PRODUCT -> CURRENT AI RESULT and never
     previous result -> new result, however many refinements deep the customer
     goes. */
  originalImage: null,
  compareMode: false,
  activeImage: 'generated',  // which single image the result view shows
  split: 50,                 // divider position, in percent from the left
  expanded: false,           // the in-dialog fallback when fullscreen is refused
};

/* One drawer session may start at most this many PAID generations. The Worker
   enforces its own limit independently; a browser value can never be the only
   thing standing between a click and a bill. */
const SESSION_GENERATION_LIMIT = 5;

/* A cache hit returns in a few hundred ms. Holding the overlay back by this
   much means an instant result never flashes VISUALISING at the customer,
   while a real generation (~11s) still shows it immediately enough. */
const OVERLAY_DELAY_MS = 400;

const DIMENSION_TERM = { width: 'wide', depth: 'deep', height: 'tall', length: 'long' };

const qs = (sel, root = document) => root.querySelector(sel);
const qsa = (sel, root = document) => [...root.querySelectorAll(sel)];

function dialog() {
  return qs('[data-homecrest-customise-dialog]');
}

function escapeHtml(s) {
  return String(s ?? '').replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]
  );
}

function titleCase(s) {
  return String(s ?? '').replace(/[_-]/g, ' ').replace(/\b\p{Ll}/gu, (c) => c.toUpperCase());
}

/* ==========================================================================
   Step machine
   ========================================================================== */

function eyebrowFor(step, d) {
  switch (step) {
    case 'describe':
      return qs('[data-hc-cust-goto="describe"]', d)?.textContent.trim().replace(/\s*→\s*$/, '') || 'Make it yours';
    case 'clarify':
      return 'One question';
    case 'design':
      return d.dataset.labelDesign || 'Your design';
    case 'failed':
      return 'Visualization';
    case 'submit':
      return d.dataset.labelSubmit || 'Love your design?';
    case 'contact':
      return 'Request a callback';
    case 'sent':
      return d.dataset.labelSent || 'Design submitted';
    default:
      return d.dataset.labelCustomise || 'Customise';
  }
}

function go(step) {
  const d = dialog();
  if (!d || !STEPS.includes(step)) return;

  S.step = step;
  d.dataset.hcStep = step;

  for (const panel of qsa('[data-hc-step-panel]', d)) panel.hidden = panel.dataset.hcStepPanel !== step;
  for (const set of qsa('[data-hc-foot]', d)) set.hidden = set.dataset.hcFoot !== step;

  const eyebrow = qs('[data-hc-cust-eyebrow]', d);
  if (eyebrow) eyebrow.textContent = eyebrowFor(step, d);

  if (step === 'describe') {
    renderSuggestions();
    qs('[data-hc-cust-request]', d)?.focus({ preventScroll: true });
  }
  if (step === 'submit') renderSubmit();

  const body = qs('.hc-cust__body', d);
  if (body) body.scrollTop = 0;
}

/* ==========================================================================
   Prompt chips - inspiration, never limits
   ========================================================================== */

const EXAMPLE_PROMPTS = [
  'Make it wider',
  'Add another section',
  'Remove a section',
  'Add more storage',
  'Change the legs',
  'Make it look more premium',
];

function renderSuggestions() {
  const d = dialog();
  const host = qs('[data-hc-cust-suggest-host]', d);
  if (!host) return;

  const label = d.dataset.labelSuggest || 'For example';
  host.hidden = false;
  host.innerHTML = `
    <p class="hc-cust__legend">${escapeHtml(label)}</p>
    <div class="hc-cust__values">
      ${EXAMPLE_PROMPTS.map(
        (c) => `<button type="button" class="hc-cust__chip" data-hc-cust-suggest="${escapeHtml(c)}">${escapeHtml(c)}</button>`
      ).join('')}
    </div>`;
}

/* ==========================================================================
   Clarification - asks, never refuses
   ========================================================================== */

function renderClarification(clarification) {
  const d = dialog();
  const host = qs('[data-hc-cust-clarify]', d);
  if (!host || !clarification) return;

  S.clarification = clarification;
  const isDimension = clarification.axis === 'dimensions';
  const heading = isDimension ? 'Which dimension?' : `Which ${titleCase(clarification.axis)}?`;
  const pillClass = isDimension ? 'hc-cust__value hc-cust__value--axis' : 'hc-cust__value';

  host.innerHTML = `
    <p class="hc-cust__question">${escapeHtml(heading)}</p>
    <p class="hc-cust__echo">You asked for &ldquo;${escapeHtml(S.lastRequest)}&rdquo;.</p>
    <div class="hc-cust__values">
      ${(clarification.options ?? [])
        .map((o) => `<button type="button" class="${pillClass}" data-hc-cust-answer="${escapeHtml(o)}">${escapeHtml(titleCase(o))}</button>`)
        .join('')}
    </div>`;

  go('clarify');
}

function answerClarification(option) {
  const c = S.clarification;
  if (!c) return;
  const text = c.axis === 'dimensions' ? `${S.lastRequest} ${DIMENSION_TERM[option] ?? option}` : option;
  S.clarification = null;
  runRequest(text);
}

/* ==========================================================================
   The design state
   ========================================================================== */

function designList(cfg, { limit = 3 } = {}) {
  const mods = cfg?.modifications ?? [];
  const shown = mods.length > limit ? mods.slice(-limit) : mods;
  const hidden = mods.length - shown.length;

  const items = shown
    .map(
      (m) => `<li class="hc-cust__design-item">
        <span>${escapeHtml(m.instruction || `${titleCase(m.axis)}: ${m.value}`)}</span>
        <button type="button" class="hc-cust__remove" data-hc-cust-remove="${escapeHtml(m.id)}" aria-label="Remove this change">&times;</button>
      </li>`
    )
    .join('');

  return `
    ${hidden > 0 ? `<p class="hc-cust__more">+ ${hidden} earlier change${hidden === 1 ? '' : 's'}</p>` : ''}
    <ul class="hc-cust__design-list">${items}</ul>`;
}

/**
 * The visualisation stage.
 *
 * Generation used to announce itself as the word "Working…" in the small
 * status line at the foot of the panel. It now happens ON the visualisation
 * area: whatever is already there stays visible underneath, blurred and
 * dimmed, with VISUALISING centred over it.
 *
 * The substrate is a REAL image every time - the previous visualisation when
 * refining, otherwise the product's own first gallery image. Nothing is
 * invented to fill the space, and the product's media is never modified.
 */
function stageMarkup(imageUrl, { generating = false, revealed = false } = {}) {
  const d = dialog();
  const word = d?.dataset.labelVisualising || 'Visualising';
  const sub = d?.dataset.copyVisualising || 'Creating your space…';
  const media = imageUrl
    ? `<img class="hc-cust__preview-img hc-cust__stage-media${revealed ? ' hc-cust__stage-media--revealed' : ''}" src="${escapeHtml(imageUrl)}" alt="Visual representation of your customised design">`
    : '';

  return `
    <div class="hc-cust__stage" data-hc-cust-stage data-generating="${generating ? 'true' : 'false'}" aria-busy="${generating ? 'true' : 'false'}">
      ${media}
      <div class="hc-cust__visualising" data-hc-cust-visualising>
        <p class="hc-cust__visualising-word">${escapeHtml(word)}</p>
        <span class="hc-cust__visualising-rule" aria-hidden="true"></span>
        <p class="hc-cust__visualising-sub">${escapeHtml(sub)}</p>
      </div>
    </div>`;
}

/* ==========================================================================
   The comparison viewer
   ==========================================================================
   ORIGINAL PRODUCT -> WHAT THE AI CHANGED -> AI CUSTOMISED RESULT, in one
   surface. The interaction is the one already proven on Design Your Own: two
   images stacked, the AI one clipped to a divider, and a transparent range
   input over the whole stage doing the dragging - which is what makes it
   touch-friendly and keyboard-operable without a single pointer handler.

   THIS IS A VIEWER AND NOTHING ELSE. It reads two URLs that already exist in
   memory. It issues no request, registers no provider and cannot reach
   generate(); the AI studio's own controller is neither imported nor started.

   WHY THE MARKUP IS BUILT HERE RATHER THAN BOUND ONCE
   renderDesign() rebuilds this panel with innerHTML on every result, so a
   handler attached to these nodes would die with the first refinement. Every
   control below is therefore addressed by data attribute and driven from the
   delegated listeners in bind(), which survive any number of rebuilds.
*/

/** The subset of URLs an <img> can actually display. Mirrors renderDesign. */
function renderableUrl(url) {
  return /^(https?:|data:image\/|blob:)/i.test(url ?? '') ? String(url) : '';
}

function thumbMarkup(which, url, label) {
  const has = Boolean(url);
  return `
    <li class="hc-cust__thumb-item">
      <button type="button" class="hc-cust__thumb" data-hc-cust-thumb="${which}" aria-pressed="false" ${has ? '' : 'disabled'}>
        <span class="hc-cust__thumb-frame">
          <img class="hc-cust__thumb-img" data-hc-cust-thumb-img="${which}" src="${escapeHtml(url)}" alt="" ${has ? '' : 'hidden'}>
          <span class="hc-cust__thumb-empty" data-hc-cust-thumb-empty="${which}" aria-hidden="true" ${has ? 'hidden' : ''}></span>
        </span>
        <span class="hc-cust__thumb-label">${escapeHtml(label)}</span>
      </button>
    </li>`;
}

/**
 * The whole result surface: view switch, tools, stage, thumbnails, before/after.
 *
 * The generated image is rendered FIRST and keeps `hc-cust__stage-media`, so
 * substrateImage() below goes on reading exactly what it always read - the
 * latest result - no matter which view the customer happens to be in.
 */
function comparisonMarkup(originalUrl, generatedUrl) {
  const canCompare = Boolean(originalUrl && generatedUrl);

  return `
    <div class="hc-cust__viewer" data-hc-cust-viewer data-compare="false" data-expanded="false">
      <div class="hc-cust__viewer-bar">
        <div class="hc-cust__seg" role="tablist" aria-label="Image view">
          <button type="button" class="hc-cust__seg-btn" role="tab" data-hc-cust-view="result" aria-selected="true">AI result</button>
          <button type="button" class="hc-cust__seg-btn" role="tab" data-hc-cust-view="compare" aria-selected="false" ${canCompare ? '' : 'disabled'}>Compare</button>
        </div>
        <div class="hc-cust__tools">
          <a class="hc-cust__tool" data-hc-cust-openfull href="${escapeHtml(generatedUrl || originalUrl)}" target="_blank" rel="noopener noreferrer">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.3" aria-hidden="true"><path d="M9.5 2.5h4v4M13.5 2.5 8 8M11 9.5v3.5a.5.5 0 0 1-.5.5H3a.5.5 0 0 1-.5-.5V5.5A.5.5 0 0 1 3 5h3.5"/></svg>
            Open full size
          </a>
          <button type="button" class="hc-cust__tool" data-hc-cust-fullscreen>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.3" aria-hidden="true"><path d="M2 6V2h4M14 6V2h-4M2 10v4h4M14 10v4h-4"/></svg>
            <span data-hc-cust-fullscreen-label>Fullscreen</span>
          </button>
        </div>
      </div>

      <div class="hc-cust__stage hc-cust__stage--viewer" data-hc-cust-stage data-generating="false" aria-busy="false">
        <img class="hc-cust__preview-img hc-cust__stage-media hc-cust__stage-media--revealed"
          data-hc-cust-result src="${escapeHtml(generatedUrl)}"
          alt="Visual representation of your customised design">

        <img class="hc-cust__preview-img hc-cust__origin-img" data-hc-cust-origin
          src="${escapeHtml(originalUrl)}" alt="The original product" hidden>

        <div class="hc-cust__compare" data-hc-cust-compare hidden>
          <img class="hc-cust__compare-img" data-hc-cust-compare-before src="${escapeHtml(originalUrl)}" alt="" aria-hidden="true">
          <div class="hc-cust__compare-after" data-hc-cust-compare-after>
            <img class="hc-cust__compare-img" data-hc-cust-compare-after-img src="${escapeHtml(generatedUrl)}" alt="" aria-hidden="true">
          </div>
          <span class="hc-cust__compare-tag hc-cust__compare-tag--before">Original</span>
          <span class="hc-cust__compare-tag hc-cust__compare-tag--after">AI generated</span>
          <input class="hc-cust__compare-range" type="range" min="0" max="100" step="1" value="${S.split}"
            data-hc-cust-range aria-label="Drag to reveal the original or the AI result">
          <span class="hc-cust__compare-handle" data-hc-cust-handle aria-hidden="true"></span>
        </div>
      </div>

      <div class="hc-cust__viewer-foot">
        <ul class="hc-cust__thumbs">
          ${thumbMarkup('original', originalUrl, 'Original')}
          ${thumbMarkup('generated', generatedUrl, 'AI generated')}
        </ul>

        <div class="hc-cust__ba">
          <span class="hc-cust__ba-label" data-hc-cust-ba-label="before">Before</span>
          <button type="button" class="hc-cust__ba-btn" data-hc-cust-ba data-showing="generated"
            ${canCompare ? '' : 'disabled'} aria-label="Show the original product">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M5 3l5 5-5 5"/></svg>
          </button>
          <span class="hc-cust__ba-label" data-hc-cust-ba-label="after" data-active="true">After</span>
        </div>
      </div>
    </div>`;
}

/** Moves the divider. Style only - no layout is read, so dragging stays cheap. */
function setSplit(value) {
  const pct = Math.min(100, Math.max(0, Number(value) || 0));
  S.split = pct;

  const compare = qs('[data-hc-cust-compare]', dialog());
  if (!compare) return;
  compare.style.setProperty('--hc-split', `${pct}%`);
  const after = qs('[data-hc-cust-compare-after]', compare);
  if (after) after.style.clipPath = `inset(0 0 0 ${pct}%)`;
  const handle = qs('[data-hc-cust-handle]', compare);
  if (handle) handle.style.left = `${pct}%`;
}

/**
 * Brings the rendered viewer into line with S. Called after a rebuild and
 * after every control, IN PLACE - switching view must not re-run innerHTML,
 * or the range input would be replaced mid-drag.
 */
function syncViewer() {
  const viewer = qs('[data-hc-cust-viewer]', dialog());
  if (!viewer) return;

  const originalUrl = S.originalImage || '';
  const generatedUrl = renderableUrl(S.preview?.image_url);
  const canCompare = Boolean(originalUrl && generatedUrl);
  const comparing = S.compareMode && canCompare;
  const showingGenerated = !(S.activeImage === 'original' && originalUrl);

  viewer.dataset.compare = String(comparing);
  viewer.dataset.expanded = String(S.expanded);

  const show = (node, on) => { if (node) node.hidden = !on; };

  /* While comparing, the result image STAYS IN FLOW. It is what gives the
     stage its height - the comparison itself is an absolutely positioned,
     opaque overlay and contributes no size, so hiding the image underneath
     would collapse the stage to its min-height the moment Compare is
     selected. */
  show(qs('[data-hc-cust-result]', viewer), comparing || showingGenerated);
  show(qs('[data-hc-cust-origin]', viewer), !comparing && !showingGenerated);
  show(qs('[data-hc-cust-compare]', viewer), comparing);

  for (const btn of qsa('[data-hc-cust-view]', viewer)) {
    const isCompare = btn.dataset.hcCustView === 'compare';
    btn.setAttribute('aria-selected', String(isCompare ? comparing : !comparing));
    if (isCompare) btn.disabled = !canCompare;
  }

  for (const btn of qsa('[data-hc-cust-thumb]', viewer)) {
    const which = btn.dataset.hcCustThumb;
    const url = which === 'original' ? originalUrl : generatedUrl;
    btn.disabled = !url;
    btn.setAttribute(
      'aria-pressed',
      String(!comparing && url ? (which === 'generated') === showingGenerated : false)
    );
  }

  const ba = qs('[data-hc-cust-ba]', viewer);
  if (ba) {
    ba.disabled = !canCompare;
    ba.dataset.showing = showingGenerated ? 'generated' : 'original';
    ba.setAttribute('aria-label', showingGenerated ? 'Show the original product' : 'Show the AI result');
  }
  const beforeLabel = qs('[data-hc-cust-ba-label="before"]', viewer);
  const afterLabel = qs('[data-hc-cust-ba-label="after"]', viewer);
  if (beforeLabel) beforeLabel.dataset.active = String(!showingGenerated);
  if (afterLabel) afterLabel.dataset.active = String(showingGenerated);

  /* Cross-origin images cannot be force-saved by the browser, so this opens
     the picture at full resolution rather than promising a download that the
     browser would silently turn into a navigation. */
  const open = qs('[data-hc-cust-openfull]', viewer);
  if (open) {
    const href = comparing || showingGenerated ? generatedUrl || originalUrl : originalUrl || generatedUrl;
    if (href) open.setAttribute('href', href);
    else open.removeAttribute('href');
  }

  const fsLabel = qs('[data-hc-cust-fullscreen-label]', viewer);
  if (fsLabel) fsLabel.textContent = S.expanded || document.fullscreenElement ? 'Exit fullscreen' : 'Fullscreen';

  setSplit(S.split);
}

/** The in-dialog fallback for when the browser refuses fullscreen in a modal. */
function setExpanded(on) {
  S.expanded = Boolean(on);
  syncViewer();
}

/**
 * Fullscreen, attempted on the stage and never allowed to throw.
 *
 * A <dialog> lives in the top layer and browsers disagree about fullscreening
 * something inside it, so both the synchronous throw and the rejected promise
 * fall back to the expanded in-dialog state. The drawer is never left broken.
 */
function toggleFullscreen() {
  if (S.expanded) {
    setExpanded(false);
    return;
  }
  if (document.fullscreenElement) {
    try {
      const exit = document.exitFullscreen?.();
      if (exit && typeof exit.catch === 'function') exit.catch(() => {});
    } catch {
      /* nothing to exit */
    }
    return;
  }

  const stage = qs('[data-hc-cust-stage]', dialog());
  if (!stage || typeof stage.requestFullscreen !== 'function') {
    setExpanded(true);
    return;
  }
  try {
    const request = stage.requestFullscreen();
    if (request && typeof request.catch === 'function') request.catch(() => setExpanded(true));
  } catch {
    setExpanded(true);
  }
}

/** The image to blur behind the overlay: the last one, else the product's own. */
function substrateImage() {
  const P = window.HomeCrestPreview;
  const current = qs('.hc-cust__stage-media', dialog());
  if (current?.src) return current.src;
  if (S.preview?.image_url && /^(https?:|data:image\/|blob:)/i.test(S.preview.image_url)) {
    return S.preview.image_url;
  }
  return P?.readSourceImages(document)?.[0] ?? '';
}

/**
 * The VISIBLE half of "one click is one generation". The guarantee itself is
 * the in-flight latch in generate(); this is what stops a control looking
 * clickable while a generation is running.
 *
 * EVERY control that can reach generate() must be listed here. Retry and the
 * design-list remove buttons were missing, which left both of them live during
 * a generation - a disabled <button> fires no click event, so listing them is
 * what makes the delegated handler below unreachable in the first place.
 */
function lockControls(on) {
  const d = dialog();
  if (!d) return;
  qsa(
    '[data-hc-cust-apply], [data-hc-cust-refine], [data-hc-cust-retry], [data-hc-cust-remove]',
    d
  ).forEach((el) => {
    el.disabled = on;
  });
}

function beginVisualising() {
  const d = dialog();
  const host = qs('[data-hc-cust-design]', d);
  if (!host) return;

  // The screen reader hears the message once, from the live region below -
  // never the animation frames, which are decorative.
  host.innerHTML = stageMarkup(substrateImage(), { generating: true }) + `
    <p class="hc-cust__status" data-hc-cust-status role="status" aria-live="polite"></p>`;

  lockControls(true);
  go('design');
  setStatus(d?.dataset.copyVisualising || 'Creating your space…');
}

function endVisualising() {
  lockControls(false);
  const stage = qs('[data-hc-cust-stage]', dialog());
  if (stage) {
    stage.dataset.generating = 'false';
    stage.setAttribute('aria-busy', 'false');
  }
}

function renderDesign() {
  const d = dialog();
  const host = qs('[data-hc-cust-design]', d);
  if (!host) return;

  const cfg = S.configuration;
  const P = window.HomeCrestPreview;
  const Q = window.HomeCrestQuotation;
  if (!cfg) return;

  const preview = S.preview ? P.reconcile(S.preview, cfg) : null;
  S.preview = preview;
  const displayable = preview && P.isDisplayable(preview, cfg);

  let previewBlock = '';
  if (displayable) {
    const renderable = renderableUrl(preview.image_url);
    /* With the original in hand this is a comparison; without it - a provider
       that returned a reference rather than a picture, or a PDP with no
       readable gallery - it degrades to the single image it always was. */
    previewBlock = renderable
      ? comparisonMarkup(S.originalImage || '', renderable)
      : `<p class="hc-cust__preview-ref">Preview reference: ${escapeHtml(preview.image_url)}</p>`;
  } else if (preview?.status === P.STATES.STALE) {
    previewBlock = `<p class="hc-cust__warn">This visualization is out of date because the design changed.</p>`;
  }

  host.innerHTML = `
    <div class="hc-cust__preview">
      ${previewBlock}
      <p class="hc-cust__disclaimer">${escapeHtml(Q.DISCLAIMER)}</p>
    </div>
    <div class="hc-cust__design">
      <p class="hc-cust__legend">${escapeHtml(d.dataset.labelDesign || 'Your design')}</p>
      ${designList(cfg)}
    </div>
    <div class="hc-cust__refine">
      <label class="hc-cust__legend" for="HomeCrestRefine">${escapeHtml(d.dataset.labelRefine || 'What would you like to change?')}</label>
      <input class="hc-cust__input" id="HomeCrestRefine" data-hc-cust-refine type="text" placeholder="Describe another change">
      <p class="hc-cust__status" data-hc-cust-status role="status" aria-live="polite"></p>
    </div>`;

  /* The panel was just replaced wholesale, so the fresh nodes are brought into
     line with the viewer state here - the split, the active view and the
     thumbnails all survive the rebuild. */
  syncViewer();

  // The submit CTA is enabled only when a visualisation is genuinely displayable.
  const cta = qs('[data-hc-foot="design"] [data-hc-cust-goto="submit"]', d);
  if (cta) cta.disabled = !Q.canSubmit(cfg, preview).ok;
}

/**
 * The session limit. Reuses the failure panel's shape so no new surface is
 * introduced, but says something quite different from a failure: nothing went
 * wrong, the customer has simply reached the ceiling for this session.
 */
function renderLimited() {
  const d = dialog();
  const host = qs('[data-hc-cust-limited]', d) ?? qs('[data-hc-cust-failed]', d);
  if (!host) return;

  lockControls(false);
  host.innerHTML = `
    <p class="hc-cust__sent-title">${escapeHtml(d.dataset.labelLimited || "You've reached the visualisation limit for this session.")}</p>
    <p class="hc-cust__sent-copy">${escapeHtml(d.dataset.copyLimited || 'Please continue with your current design, or try again later.')}</p>`;
  go('failed');
}

/**
 * The failure state renders ONLY the approved customer copy.
 *
 * A provider or Worker message - "gemini call failed: 429", "rate limit
 * exceeded", an HTTP status, an internal code - is diagnostic text written for
 * us, not for a customer, and some of it names infrastructure a customer
 * should not be told about. It goes to the console and never into the DOM.
 */
function renderFailed(diagnostic) {
  const d = dialog();
  lockControls(false);
  const host = qs('[data-hc-cust-failed]', d);
  if (!host) return;

  if (diagnostic) console.warn('HomeCrest preview failed:', diagnostic);

  host.innerHTML = `
    <p class="hc-cust__sent-title">${escapeHtml(d.dataset.labelFailed || "We couldn't generate this visualization.")}</p>
    <p class="hc-cust__sent-copy">${escapeHtml(d.dataset.copyFailed || '')}</p>`;
  go('failed');
}

function renderSubmit() {
  const d = dialog();
  const host = qs('[data-hc-cust-submit]', d);
  const cfg = S.configuration;
  if (!host || !cfg) return;

  host.innerHTML = `
    <p class="hc-cust__sent-title">${escapeHtml(d.dataset.labelSubmit || 'Love your design?')}</p>
    <p class="hc-cust__sent-copy">${escapeHtml(d.dataset.copySubmit || '')}</p>
    <div class="hc-cust__design">
      <p class="hc-cust__legend">${escapeHtml(d.dataset.labelDesign || 'Your design')}</p>
      ${designList(cfg, { limit: 3 })}
    </div>
    <button type="button" class="hc-cust__link hc-cust__link--block" data-hc-cust-goto="contact">
      ${escapeHtml(d.dataset.labelCallback || 'Request a callback')}
    </button>
    <p class="hc-cust__disclaimer">${escapeHtml(window.HomeCrestQuotation.DISCLAIMER)}</p>`;
}

function renderSent(channelLabel) {
  const d = dialog();
  const host = qs('[data-hc-cust-sent]', d);
  if (!host) return;

  host.innerHTML = `
    <p class="hc-cust__sent-title">${escapeHtml(d.dataset.labelSent || 'Design submitted')}</p>
    <p class="hc-cust__sent-copy">${escapeHtml(d.dataset.copySent || '')}</p>
    ${channelLabel ? `<p class="hc-cust__status">${escapeHtml(channelLabel)}</p>` : ''}
    <p class="hc-cust__disclaimer">${escapeHtml(window.HomeCrestQuotation.DISCLAIMER)}</p>`;
  go('sent');
}

/* ==========================================================================
   Pipeline
   ========================================================================== */

function setStatus(text) {
  const el = qs('[data-hc-cust-status]', dialog());
  if (el) el.textContent = text ?? '';
}

async function runRequest(text) {
  const C = window.HomeCrestConfiguration;
  const P = window.HomeCrestPreview;
  const Q = window.HomeCrestQuotation;
  if (!C || !P || !Q || S.busy) return;

  S.busy = true;
  /* Locked here, in the same task as the click that started it, so the second
     click of a burst already meets a disabled control rather than relying on
     `S.busy` to swallow it silently. `S.busy` remains the guarantee; this is
     what makes the guarantee visible. */
  lockControls(true);
  S.lastRequest = text;
  setStatus(dialog()?.dataset.labelWorking || 'One moment…');

  try {
    const result = await C.create({ request: text, root: document, stack: S.stack });

    if (!result.ok) {
      // Only real product errors reach here - never a capability refusal.
      setStatus(result.errors?.[0]?.message ?? 'That could not be applied.');
      return;
    }

    const v = result.validated;

    if (v.needs_clarification.length && !v.modifications.length) {
      setStatus('');
      renderClarification(v.needs_clarification[0]);
      return;
    }

    S.stack = result.modifications;
    S.selections = v.selections;
    S.configuration = result.configuration;

    if (S.configuration.fulfilment !== 'customised') {
      // Pure variant change: the page behaves exactly as it always has.
      Q.applyPurchaseGate(S.configuration, document);
      go('describe');
      setStatus(
        v.selections.length
          ? `Applied: ${v.selections.map((s) => `${titleCase(s.axis)} — ${s.value}`).join(', ')}.`
          : 'Nothing to change.'
      );
      return;
    }

    Q.applyPurchaseGate(S.configuration, document);
    await generate();
  } finally {
    S.busy = false;
  }
}

/* THE re-entrancy latch.

   generate() is reachable from three places - Visualise, Retry, and removing a
   change - and only the first of them ran inside runRequest()'s `S.busy`
   window. Retry called it bare, so a burst of clicks on the failure panel
   started a burst of concurrent generations, each with its own generation_id.
   The Worker's in-flight lock still collapsed those to one paid call, but the
   browser should not be sending them at all: the front end is the cheap place
   to stop a duplicate, and the Worker should be the backstop, not the fence.

   A second call while one is running returns the SAME promise rather than
   starting anything, so ten clicks are one generation and every caller still
   awaits a correct result. */
let inFlightGeneration = null;

function generate() {
  if (inFlightGeneration) return inFlightGeneration;
  inFlightGeneration = runGeneration().finally(() => {
    inFlightGeneration = null;
  });
  return inFlightGeneration;
}

async function runGeneration() {
  const P = window.HomeCrestPreview;
  const cfg = S.configuration;
  if (!cfg) return;

  const sources = P.readSourceImages(document);

  /* The comparison's left-hand side. readSourceImages() reads the PDP gallery
     every time and never a result, so this is the product photograph the
     customer started from whether they are on their first change or their
     fifth. A refinement therefore compares ORIGINAL -> NEWEST, never
     previous result -> newest. Display only: `sources` itself is passed to the
     provider below exactly as before. */
  if (sources.length) S.originalImage = sources[0];

  /* NO-CHANGE PROTECTION. If the image already on screen was generated from
     exactly this configuration, nothing has changed and there is nothing to
     pay for. No request leaves the browser at all, and the overlay is never
     shown, because no generation is happening. */
  if (
    S.preview?.status === P.STATES.READY &&
    S.preview.generated_from === cfg.configuration_hash
  ) {
    renderDesign();
    go('design');
    setStatus('');
    return;
  }

  /* SESSION LIMIT. Checked before the request, so a customer who has hit the
     ceiling is told rather than silently costing another generation. */
  if (S.generations >= SESSION_GENERATION_LIMIT) {
    renderLimited();
    return;
  }

  /* The controls lock on THIS tick, not when the overlay appears. The latch
     above already stops a second request, but a button that still looks
     clickable for 400ms invites the click the latch then swallows. */
  lockControls(true);

  /* The overlay is raised on a timer, not immediately: a server-side cache hit
     comes back too fast to be worth animating, and showing VISUALISING for a
     result that cost nothing misrepresents what happened. */
  let overlayTimer = setTimeout(beginVisualising, OVERLAY_DELAY_MS);

  try {
    S.preview = await P.generate(cfg, sources, { productTitle: cfg.product_title ?? document.title });

    /* Counted AFTER the result, and only when the proxy actually generated.
       A server-side cache hit costs nothing, so charging it against the
       session ceiling would lock a customer out of the feature for refining
       their way back to a design they had already seen. The Worker applies the
       same rule - its own budget is spent only on a cache miss - so the two
       counters now agree instead of the browser being the stricter of the
       two. The latch guarantees this runs one at a time, so incrementing after
       the await cannot race a second request past the limit. */
    if (S.preview?.cached !== true) S.generations += 1;
  } finally {
    clearTimeout(overlayTimer);
    // A failure must never leave the interface blurred.
    endVisualising();
  }

  if (S.preview.status === P.STATES.FAILED) {
    renderFailed(S.preview.error?.message ?? '');
    return;
  }

  /* A new result is a new comparison: recentre the divider and put the result
     itself back in front. Whether the customer was comparing is left alone -
     someone who opened Compare to study a change expects to still be comparing
     after the next one. */
  S.split = 50;
  S.activeImage = 'generated';

  renderDesign();
  go('design');
  setStatus('');
}

/**
 * Rebuilds after the customer edits the design list, then regenerates.
 *
 * Guarded like runRequest(): removing two changes in quick succession used to
 * start two rebuilds, and because each produces a DIFFERENT configuration hash
 * the Worker's in-flight lock would not have collapsed them - two distinct
 * designs are two distinct generations. The lock is taken before the first
 * await so the second click meets it.
 */
async function rebuildAndGenerate() {
  const C = window.HomeCrestConfiguration;
  const Q = window.HomeCrestQuotation;
  if (S.busy) return;

  if (!S.stack.length) {
    reset();
    return;
  }

  S.busy = true;
  lockControls(true);
  try {
    S.configuration = await C.rebuild({ stack: S.stack, selections: S.selections, root: document });
    Q.applyPurchaseGate(S.configuration, document);
    await generate();
  } finally {
    S.busy = false;
  }
}

/* ==========================================================================
   Submission
   ========================================================================== */

function productContext() {
  return {
    title: qs('.hc-pdp-editorial h1')?.textContent.trim() ?? S.configuration?.product_title,
    url: location.origin + location.pathname,
    variantTitle: qsa('.hc-pdp-editorial variant-picker .variant-option input:checked').map((i) => i.value).join(' / '),
  };
}

async function submitWhatsApp() {
  const Q = window.HomeCrestQuotation;
  const result = await Q.send({
    configuration: S.configuration,
    preview: S.preview,
    product: productContext(),
    channel: 'whatsapp',
    submissionType: 'design',
  });

  if (!result.ok) {
    renderSubmit();
    setStatus(result.error.message);
    return result;
  }
  renderSent('Opened in WhatsApp — press send to reach HomeCrest.');
  return result;
}

async function submitContact() {
  const d = dialog();
  const Q = window.HomeCrestQuotation;
  const status = qs('[data-hc-contact-status]', d);
  const read = (k) => qs(`[data-hc-contact="${k}"]`, d)?.value.trim() ?? '';

  if (read('company')) return { ok: false }; // honeypot

  const contact = { name: read('name'), phone: read('phone'), email: read('email') };
  if (!contact.name || !contact.phone) {
    if (status) status.textContent = 'Please add your name and phone number.';
    return { ok: false };
  }

  if (status) status.textContent = 'Sending…';

  /* A failed visualisation can still reach HomeCrest for help - but it is sent
     as an ASSISTANCE enquiry, never as a design submission. */
  const isDesign = Q.canSubmit(S.configuration, S.preview).ok;

  const result = await Q.send({
    configuration: S.configuration,
    preview: S.preview,
    product: productContext(),
    contact,
    channel: 'contact',
    submissionType: isDesign ? 'design' : 'assistance',
  });

  if (!result.ok) {
    if (status) status.textContent = result.error.message;
    return result;
  }
  renderSent('HomeCrest will be in touch.');
  return result;
}

/* ==========================================================================
   Reset
   ========================================================================== */

function reset() {
  S.stack = [];
  S.selections = [];
  S.configuration = null;
  S.preview = null;
  S.lastRequest = '';
  S.clarification = null;
  // The viewer goes back to its opening state along with everything else.
  S.originalImage = null;
  S.compareMode = false;
  S.activeImage = 'generated';
  S.split = 50;
  S.expanded = false;
  window.HomeCrestQuotation?.clearPurchaseGate(document);
  const d = dialog();
  const ta = qs('[data-hc-cust-request]', d);
  if (ta) ta.value = '';
  for (const host of ['[data-hc-cust-design]', '[data-hc-cust-failed]', '[data-hc-cust-submit]', '[data-hc-cust-sent]']) {
    const el = qs(host, d);
    if (el) el.innerHTML = '';
  }
  setStatus('');
  go('options');
}

/* ==========================================================================
   Binding
   ========================================================================== */

function bind() {
  if (document.documentElement.dataset.hcFlowBound === 'true') return;
  document.documentElement.dataset.hcFlowBound = 'true';

  document.addEventListener('click', (event) => {
    const t = event.target;
    if (!(t instanceof Element)) return;

    /* ------------------------------------------------------ viewer controls
       Delegated, because renderDesign() rebuilds these nodes on every result.
       None of them can reach generate(): they change what is displayed and
       nothing else, which is why none of them appear in lockControls either.
       "Open full size" is a plain link and is deliberately absent - the
       browser opens it. */
    const view = t.closest('[data-hc-cust-view]');
    if (view) {
      S.compareMode = view.dataset.hcCustView === 'compare';
      syncViewer();
      return;
    }

    const thumb = t.closest('[data-hc-cust-thumb]');
    if (thumb) {
      S.activeImage = thumb.dataset.hcCustThumb;
      S.compareMode = false;
      syncViewer();
      return;
    }

    if (t.closest('[data-hc-cust-ba]')) {
      S.activeImage = S.activeImage === 'generated' ? 'original' : 'generated';
      S.compareMode = false;
      syncViewer();
      return;
    }

    if (t.closest('[data-hc-cust-fullscreen]')) {
      toggleFullscreen();
      return;
    }

    const goto = t.closest('[data-hc-cust-goto]');
    if (goto) {
      go(goto.dataset.hcCustGoto);
      return;
    }

    const suggest = t.closest('[data-hc-cust-suggest]');
    if (suggest) {
      const ta = qs('[data-hc-cust-request]', dialog());
      if (ta) {
        ta.value = suggest.dataset.hcCustSuggest;
        ta.focus({ preventScroll: true });
      }
      return;
    }

    const answer = t.closest('[data-hc-cust-answer]');
    if (answer) {
      answerClarification(answer.dataset.hcCustAnswer);
      return;
    }

    const remove = t.closest('[data-hc-cust-remove]');
    if (remove) {
      S.stack = window.HomeCrestConfiguration.removeModification(S.stack, remove.dataset.hcCustRemove);
      rebuildAndGenerate();
      return;
    }

    if (t.closest('[data-hc-cust-apply]')) {
      const ta = qs('[data-hc-cust-request]', dialog());
      const text = ta instanceof HTMLTextAreaElement ? ta.value.trim() : '';
      if (text) runRequest(text);
      else setStatus('Describe the change you would like first.');
      return;
    }

    if (t.closest('[data-hc-cust-retry]')) {
      generate();
      return;
    }

    if (t.closest('[data-hc-cust-talk]')) {
      go('contact');
      return;
    }

    if (t.closest('[data-hc-cust-restart]')) {
      reset();
      return;
    }

    if (t.closest('[data-hc-cust-whatsapp]')) {
      submitWhatsApp();
      return;
    }

    if (t.closest('[data-hc-contact-send]')) {
      submitContact();
      return;
    }

    // A variant pill is a STANDARD change: it clears the design.
    if (t.closest('[data-hc-cust-value]')) {
      if (S.stack.length) reset();
      else window.HomeCrestQuotation?.clearPurchaseGate(document);
    }
  });

  // Enter in the refinement field submits it.
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-hc-cust-refine]')) return;
    event.preventDefault();
    const text = input.value.trim();
    if (text) {
      input.value = '';
      runRequest(text);
    }
  });

  /* The comparison divider. A range input gives dragging, touch and the arrow
     keys for free; delegated for the same lifecycle reason as the clicks. */
  document.addEventListener('input', (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.matches('[data-hc-cust-range]')) {
      setSplit(target.value);
    }
  });

  /* Leaving fullscreen by Escape or the browser's own control has to leave the
     button saying the right thing. */
  document.addEventListener('fullscreenchange', () => {
    if (qs('[data-hc-cust-viewer]', dialog())) syncViewer();
  });

  /* Escape closes a <dialog>. While the viewer is expanded it should collapse
     the viewer instead, so one keypress cannot throw away the design. Only
     intercepted while expanded - ordinary closing is untouched. */
  document.addEventListener(
    'cancel',
    (event) => {
      if (!S.expanded) return;
      if (!(event.target instanceof HTMLElement)) return;
      if (!event.target.matches('[data-homecrest-customise-dialog]')) return;
      event.preventDefault();
      setExpanded(false);
    },
    true
  );

  document.addEventListener('homecrest:action', (event) => {
    if (event.detail?.action === 'customise') requestAnimationFrame(reset);
  });

  document.addEventListener(
    'close',
    (event) => {
      if (event.target instanceof HTMLElement && event.target.matches('[data-homecrest-customise-dialog]')) {
        if (!S.configuration) window.HomeCrestQuotation?.clearPurchaseGate(document);
      }
    },
    true
  );
}

bind();

window.HomeCrestCustomiseFlow = {
  STEPS,
  state: () => ({ ...S }),
  go,
  runRequest,
  generate,
  rebuildAndGenerate,
  submitWhatsApp,
  submitContact,
  renderDesign,
  reset,
};
